(() => {
  if (window.MivaWishlist?.initialized) return;

  const STORAGE_KEY = 'miva_wishlist_v1';
  const POSITION_KEY = 'miva_wishlist_position_v1';
  const AVAILABILITY_TTL = 5 * 60 * 1000;
  const MAX_ITEMS = 100;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let items = [];
  let selectedIds = new Set();
  let root;
  let dialog;
  let list;
  let itemTemplate;
  let lastFocusedElement;
  let closeTimer;
  let toastTimer;
  let clearTimer;
  let availabilityRequest = 0;

  const asString = (value) => String(value ?? '').trim();
  const asNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
  };
  const asBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1';

  const safeUrl = (value, fallback = '#') => {
    const candidate = asString(value);
    if (!candidate) return fallback;
    try {
      const parsed = new URL(candidate, window.location.origin);
      if (parsed.origin !== window.location.origin) return fallback;
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (error) {
      return fallback;
    }
  };

  const safeImageUrl = (value) => {
    const candidate = asString(value);
    if (/^(https?:)?\/\//i.test(candidate) || candidate.startsWith('/')) return candidate;
    return '';
  };

  const normalizeItem = (raw) => {
    const productId = asString(raw?.productId);
    const variantId = asString(raw?.variantId);
    const title = asString(raw?.title);
    if (!productId || !variantId || !title) return null;

    return {
      productId,
      variantId,
      handle: asString(raw?.handle),
      title,
      url: safeUrl(raw?.url, '/'),
      image: safeImageUrl(raw?.image),
      price: asNumber(raw?.price),
      compareAt: asNumber(raw?.compareAt),
      available: raw?.available !== false && raw?.available !== 'false',
      addedAt: asNumber(raw?.addedAt) || Date.now(),
      checkedAt: asNumber(raw?.checkedAt)
    };
  };

  const readItems = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const source = Array.isArray(stored) ? stored : stored?.items;
      const seen = new Set();
      return (Array.isArray(source) ? source : [])
        .map(normalizeItem)
        .filter((item) => {
          if (!item || seen.has(item.productId)) return false;
          seen.add(item.productId);
          return true;
        })
        .slice(0, MAX_ITEMS);
    } catch (error) {
      return [];
    }
  };

  const persist = ({ announce = true } = {}) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, items }));
    } catch (error) {
      // The in-memory list remains usable when storage is unavailable.
    }

    syncInterface();
    if (announce) {
      document.dispatchEvent(new CustomEvent('miva:wishlist:updated', { detail: { items: [...items] } }));
    }
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'BRL';
    try {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(asNumber(cents) / 100);
    } catch (error) {
      return `R$ ${(asNumber(cents) / 100).toFixed(2).replace('.', ',')}`;
    }
  };

  const itemIndex = (productId) => items.findIndex((item) => item.productId === asString(productId));
  const isSaved = (productId) => itemIndex(productId) >= 0;

  const animateToggle = (button) => {
    button.classList.remove('is-confirming');
    void button.offsetWidth;
    button.classList.add('is-confirming');
    window.setTimeout(() => button.classList.remove('is-confirming'), 520);
  };

  const showToast = (message) => {
    const toast = document.querySelector('[data-miva-wishlist-toast]');
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.add('is-visible');
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2400);
  };

  const productUrlWithVariant = (url, variantId) => {
    try {
      const parsed = new URL(url || '/', window.location.origin);
      if (variantId) parsed.searchParams.set('variant', variantId);
      return safeUrl(parsed.href, '/');
    } catch (error) {
      return safeUrl(url, '/');
    }
  };

  const itemFromButton = (button) => {
    let variantId = asString(button.dataset.variantId);
    let available = asBoolean(button.dataset.productAvailable);

    if (button.dataset.wishlistPlacement === 'product') {
      const productInfo = button.closest('.product__info-container') || button.closest('product-info') || document;
      const variantInput = productInfo.querySelector('form[action*="/cart/add"] input[name="id"]:not([disabled])');
      const submitButton = productInfo.querySelector('[name="add"], .product-form__submit');
      if (variantInput?.value) variantId = variantInput.value;
      if (submitButton) available = !submitButton.disabled;
    }

    return normalizeItem({
      productId: button.dataset.productId,
      variantId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: productUrlWithVariant(button.dataset.productUrl, variantId),
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      compareAt: button.dataset.productCompareAt,
      available,
      addedAt: Date.now(),
      checkedAt: Date.now()
    });
  };

  const toggleItem = (button) => {
    const productId = asString(button.dataset.productId);
    const index = itemIndex(productId);
    animateToggle(button);

    if (index >= 0) {
      const [removed] = items.splice(index, 1);
      selectedIds.delete(productId);
      persist();
      showToast(`${removed.title} foi removido da lista`);
      return;
    }

    const item = itemFromButton(button);
    if (!item) {
      showToast('Não foi possível salvar este produto');
      return;
    }

    items.unshift(item);
    items = items.slice(0, MAX_ITEMS);
    if (!root?.hidden && item.available) selectedIds.add(item.productId);
    persist();
    showToast(`${item.title} foi adicionado à lista`);
  };

  const syncButtons = () => {
    document.querySelectorAll('[data-miva-wishlist-toggle]').forEach((button) => {
      const active = isSaved(button.dataset.productId);
      const title = asString(button.dataset.productTitle) || 'Produto';
      const label = active ? 'Remover da lista de desejos' : 'Adicionar à lista de desejos';
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', `${label}: ${title}`);

      const visibleLabel = button.querySelector('[data-miva-wishlist-toggle-label]');
      if (visibleLabel) visibleLabel.textContent = active ? 'Na lista de desejos' : 'Adicionar à lista de desejos';
    });
  };

  const syncCounts = () => {
    const count = items.length;
    const display = count > 99 ? '99+' : String(count);
    document.querySelectorAll('[data-miva-wishlist-count]').forEach((badge) => {
      badge.textContent = display;
    });
    document.querySelectorAll('[data-miva-wishlist-open]').forEach((button) => {
      const dragHint = button.matches('[data-miva-wishlist-floating]')
        ? '. Arraste para reposicionar'
        : '';
      button.setAttribute('aria-label', `Abrir lista de desejos com ${count} ${count === 1 ? 'produto' : 'produtos'}${dragHint}`);
    });
  };

  const setDrawerError = (message = '') => {
    const error = root?.querySelector('[data-miva-wishlist-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  };

  const updateSelection = () => {
    if (!root) return;
    const availableIds = items.filter((item) => item.available).map((item) => item.productId);
    const selected = availableIds.filter((id) => selectedIds.has(id));
    const checkAll = root.querySelector('[data-miva-wishlist-check-all]');
    const selection = root.querySelector('[data-miva-wishlist-selection]');
    const addButton = root.querySelector('[data-miva-wishlist-add-selected]');
    const addLabel = root.querySelector('[data-miva-wishlist-add-label]');

    if (checkAll) {
      checkAll.disabled = availableIds.length === 0;
      checkAll.checked = availableIds.length > 0 && selected.length === availableIds.length;
      checkAll.indeterminate = selected.length > 0 && selected.length < availableIds.length;
    }
    if (selection) selection.textContent = `${selected.length} ${selected.length === 1 ? 'produto selecionado' : 'produtos selecionados'}`;
    if (addButton) addButton.disabled = selected.length === 0;
    if (addLabel) addLabel.textContent = selected.length === 1 ? 'Adicionar selecionado ao carrinho' : 'Adicionar selecionados ao carrinho';
  };

  const renderDrawer = () => {
    if (!root || !list || !itemTemplate) return;

    const empty = root.querySelector('[data-miva-wishlist-empty]');
    const toolbar = root.querySelector('[data-miva-wishlist-toolbar]');
    const footer = root.querySelector('[data-miva-wishlist-footer]');
    const summary = root.querySelector('[data-miva-wishlist-summary]');
    const hasItems = items.length > 0;

    if (empty) empty.hidden = hasItems;
    if (toolbar) toolbar.hidden = !hasItems;
    if (footer) footer.hidden = !hasItems;
    if (summary) summary.textContent = `${items.length} ${items.length === 1 ? 'produto salvo' : 'produtos salvos'}`;
    list.replaceChildren();

    items.forEach((item) => {
      const fragment = itemTemplate.content.cloneNode(true);
      const element = fragment.querySelector('[data-miva-wishlist-item]');
      const checkbox = fragment.querySelector('[data-miva-wishlist-item-check]');
      const media = fragment.querySelector('[data-miva-wishlist-item-link]');
      const image = fragment.querySelector('[data-miva-wishlist-item-image]');
      const title = fragment.querySelector('[data-miva-wishlist-item-title]');
      const price = fragment.querySelector('[data-miva-wishlist-item-price]');
      const compare = fragment.querySelector('[data-miva-wishlist-item-compare]');
      const availability = fragment.querySelector('[data-miva-wishlist-item-availability]');
      const remove = fragment.querySelector('[data-miva-wishlist-item-remove]');

      element.dataset.productId = item.productId;
      checkbox.dataset.productId = item.productId;
      checkbox.checked = item.available && selectedIds.has(item.productId);
      checkbox.disabled = !item.available;
      checkbox.setAttribute('aria-label', `Selecionar ${item.title}`);

      const productUrl = safeUrl(item.url, '/');
      media.href = productUrl;
      title.href = productUrl;
      title.textContent = item.title;
      price.textContent = formatMoney(item.price);

      if (item.compareAt > item.price) {
        compare.textContent = formatMoney(item.compareAt);
        compare.hidden = false;
      }

      if (item.image) {
        image.src = item.image;
        image.alt = item.title;
      } else {
        media.classList.add('is-fallback');
      }

      availability.textContent = item.available ? 'Disponível' : 'Indisponível no momento';
      availability.classList.toggle('is-unavailable', !item.available);
      remove.dataset.productId = item.productId;
      remove.setAttribute('aria-label', `Remover ${item.title} da lista`);
      list.appendChild(fragment);
    });

    updateSelection();
  };

  const syncInterface = () => {
    syncButtons();
    syncCounts();
    renderDrawer();
  };

  const refreshItem = async (item) => {
    if (!item.handle || Date.now() - item.checkedAt < AVAILABILITY_TTL) return item;
    const rootRoute = window.Shopify?.routes?.root || window.theme?.routes?.root_url || '/';
    const productUrl = `${rootRoute}products/${encodeURIComponent(item.handle)}.js`;

    try {
      const response = await fetch(productUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return item;
      const product = await response.json();
      const variant = Array.isArray(product.variants)
        ? product.variants.find((candidate) => String(candidate.id) === item.variantId)
        : null;
      if (!variant) return { ...item, available: false, checkedAt: Date.now() };

      const variantImage = variant.featured_image?.src || variant.featured_image || product.featured_image || item.image;
      return {
        ...item,
        price: asNumber(variant.price),
        compareAt: asNumber(variant.compare_at_price),
        available: variant.available !== false,
        image: safeImageUrl(variantImage) || item.image,
        checkedAt: Date.now()
      };
    } catch (error) {
      return item;
    }
  };

  const refreshAvailability = async () => {
    const requestId = ++availabilityRequest;
    if (!items.length) return;
    list?.setAttribute('aria-busy', 'true');
    const refreshed = await Promise.all(items.map(refreshItem));
    if (requestId !== availabilityRequest) return;
    items = refreshed.map(normalizeItem).filter(Boolean);
    items.filter((item) => !item.available).forEach((item) => selectedIds.delete(item.productId));
    list?.removeAttribute('aria-busy');
    persist({ announce: false });
  };

  const openDrawer = () => {
    if (!root || !dialog) return;
    window.clearTimeout(closeTimer);
    items = readItems();
    selectedIds = new Set(items.filter((item) => item.available).map((item) => item.productId));
    setDrawerError();
    renderDrawer();
    lastFocusedElement = document.activeElement;
    root.hidden = false;
    document.body.classList.add('miva-wishlist--open');
    window.requestAnimationFrame(() => {
      root.classList.add('is-open');
      dialog.focus({ preventScroll: true });
    });
    refreshAvailability();
  };

  const closeDrawer = ({ immediate = false, restoreFocus = true } = {}) => {
    if (!root || root.hidden) return;
    window.clearTimeout(closeTimer);
    root.classList.remove('is-open');
    document.body.classList.remove('miva-wishlist--open');

    const finish = () => {
      root.hidden = true;
      if (restoreFocus && lastFocusedElement?.isConnected) lastFocusedElement.focus({ preventScroll: true });
    };
    if (immediate || reducedMotion.matches) finish();
    else closeTimer = window.setTimeout(finish, 270);
  };

  const removeItem = (productId) => {
    const index = itemIndex(productId);
    if (index < 0) return;
    const [removed] = items.splice(index, 1);
    selectedIds.delete(removed.productId);
    persist();
    showToast(`${removed.title} foi removido da lista`);
  };

  const clearList = (button) => {
    if (button.dataset.confirming !== 'true') {
      button.dataset.confirming = 'true';
      button.textContent = 'Confirmar limpeza';
      window.clearTimeout(clearTimer);
      clearTimer = window.setTimeout(() => {
        button.dataset.confirming = 'false';
        button.textContent = 'Limpar lista';
      }, 3200);
      return;
    }

    window.clearTimeout(clearTimer);
    button.dataset.confirming = 'false';
    button.textContent = 'Limpar lista';
    items = [];
    selectedIds.clear();
    persist();
    showToast('Lista de desejos limpa');
  };

  const addSelectedToCart = async (button) => {
    const chosen = items.filter((item) => item.available && selectedIds.has(item.productId));
    if (!chosen.length) return;

    setDrawerError();
    button.disabled = true;
    button.classList.add('is-loading');
    const miniCart = document.querySelector('mini-cart');
    const sections = typeof miniCart?.getSectionsToRender === 'function'
      ? miniCart.getSectionsToRender().map((section) => section.id)
      : [];
    const payload = {
      items: chosen.map((item) => ({ id: Number(item.variantId), quantity: 1 })),
      sections,
      sections_url: window.location.pathname
    };

    try {
      const endpoint = window.theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Não foi possível adicionar os produtos.');

      closeDrawer({ immediate: true, restoreFocus: false });
      if (miniCart && result.sections && typeof miniCart.renderContents === 'function') {
        miniCart.renderContents(result);
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        if (document.body.classList.contains('template-cart')) window.location.reload();
      }
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: result } }));
      showToast(`${chosen.length} ${chosen.length === 1 ? 'produto adicionado' : 'produtos adicionados'} ao carrinho`);
    } catch (error) {
      setDrawerError(error?.message || 'Não foi possível adicionar os produtos. Tente novamente.');
    } finally {
      button.classList.remove('is-loading');
      updateSelection();
    }
  };

  const focusableElements = () => {
    if (!dialog) return [];
    return Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => element.offsetParent !== null);
  };

  const trapKeyboard = (event) => {
    if (!root || root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = focusableElements();
    if (!focusable.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleClick = (event) => {
    const toggle = event.target.closest?.('[data-miva-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleItem(toggle);
      return;
    }

    const open = event.target.closest?.('[data-miva-wishlist-open]');
    if (open) {
      event.preventDefault();
      openDrawer();
      return;
    }

    const close = event.target.closest?.('[data-miva-wishlist-close]');
    if (close) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const remove = event.target.closest?.('[data-miva-wishlist-item-remove]');
    if (remove) {
      event.preventDefault();
      removeItem(remove.dataset.productId);
      return;
    }

    const clear = event.target.closest?.('[data-miva-wishlist-clear]');
    if (clear) {
      event.preventDefault();
      clearList(clear);
      return;
    }

    const add = event.target.closest?.('[data-miva-wishlist-add-selected]');
    if (add) {
      event.preventDefault();
      addSelectedToCart(add);
    }
  };

  const handleChange = (event) => {
    const itemCheckbox = event.target.closest?.('[data-miva-wishlist-item-check]');
    if (itemCheckbox) {
      if (itemCheckbox.checked) selectedIds.add(itemCheckbox.dataset.productId);
      else selectedIds.delete(itemCheckbox.dataset.productId);
      updateSelection();
      return;
    }

    const checkAll = event.target.closest?.('[data-miva-wishlist-check-all]');
    if (checkAll) {
      items.filter((item) => item.available).forEach((item) => {
        if (checkAll.checked) selectedIds.add(item.productId);
        else selectedIds.delete(item.productId);
      });
      renderDrawer();
    }
  };

  const updateProductVariant = (variant) => {
    if (!variant?.id) return;
    document.querySelectorAll('[data-miva-wishlist-toggle][data-wishlist-placement="product"]').forEach((button) => {
      button.dataset.variantId = String(variant.id);
      button.dataset.productPrice = String(variant.price || 0);
      button.dataset.productCompareAt = String(variant.compare_at_price || 0);
      button.dataset.productAvailable = variant.available === false ? 'false' : 'true';

      const image = variant.featured_media?.preview_image?.src || variant.featured_image?.src || variant.featured_image;
      if (image) button.dataset.productImage = image;

      const index = itemIndex(button.dataset.productId);
      if (index >= 0) {
        const current = itemFromButton(button);
        if (current) {
          items[index] = { ...items[index], ...current, addedAt: items[index].addedAt };
          persist({ announce: false });
        }
      }
    });
  };

  const restoreFloatingPosition = (floating) => {
    try {
      const saved = JSON.parse(localStorage.getItem(POSITION_KEY) || 'null');
      if (!saved || !Number.isFinite(saved.x) || !Number.isFinite(saved.y)) return false;
      const margin = 12;
      const rect = floating.getBoundingClientRect();
      const maxLeft = Math.max(margin, window.innerWidth - rect.width - margin);
      const maxTop = Math.max(margin, window.innerHeight - rect.height - margin);
      const left = margin + Math.min(1, Math.max(0, saved.x)) * (maxLeft - margin);
      const top = margin + Math.min(1, Math.max(0, saved.y)) * (maxTop - margin);
      floating.style.left = `${Math.round(left)}px`;
      floating.style.top = `${Math.round(top)}px`;
      floating.classList.add('is-positioned');
      return true;
    } catch (error) {
      return false;
    }
  };

  const initFloatingDrag = () => {
    const floating = document.querySelector('[data-miva-wishlist-floating]');
    if (!floating || floating.dataset.dragReady === 'true') return;
    floating.dataset.dragReady = 'true';
    restoreFloatingPosition(floating);
    let drag;
    let suppressClick = false;

    const clamp = (left, top) => {
      const rect = floating.getBoundingClientRect();
      const margin = 12;
      return {
        left: Math.min(Math.max(margin, left), Math.max(margin, window.innerWidth - rect.width - margin)),
        top: Math.min(Math.max(margin, top), Math.max(margin, window.innerHeight - rect.height - margin))
      };
    };

    const savePosition = () => {
      const rect = floating.getBoundingClientRect();
      const margin = 12;
      const widthRange = Math.max(1, window.innerWidth - rect.width - margin * 2);
      const heightRange = Math.max(1, window.innerHeight - rect.height - margin * 2);
      try {
        localStorage.setItem(POSITION_KEY, JSON.stringify({
          x: Math.min(1, Math.max(0, (rect.left - margin) / widthRange)),
          y: Math.min(1, Math.max(0, (rect.top - margin) / heightRange))
        }));
      } catch (error) {
        // Position persistence is optional.
      }
    };

    floating.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      const rect = floating.getBoundingClientRect();
      drag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top, moved: false };
      floating.setPointerCapture?.(event.pointerId);
    });

    floating.addEventListener('pointermove', (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - drag.x;
      const deltaY = event.clientY - drag.y;
      if (!drag.moved && Math.hypot(deltaX, deltaY) < 6) return;
      drag.moved = true;
      const position = clamp(drag.left + deltaX, drag.top + deltaY);
      floating.classList.add('is-positioned', 'is-dragging');
      floating.style.left = `${Math.round(position.left)}px`;
      floating.style.top = `${Math.round(position.top)}px`;
    });

    const finishDrag = (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      floating.releasePointerCapture?.(event.pointerId);
      if (drag.moved) {
        suppressClick = true;
        savePosition();
        window.setTimeout(() => { suppressClick = false; }, 0);
      }
      floating.classList.remove('is-dragging');
      drag = null;
    };

    floating.addEventListener('pointerup', finishDrag);
    floating.addEventListener('pointercancel', finishDrag);
    floating.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    const handleResize = () => {
      if (!floating.classList.contains('is-positioned')) return;
      const rect = floating.getBoundingClientRect();
      const position = clamp(rect.left, rect.top);
      floating.style.left = `${Math.round(position.left)}px`;
      floating.style.top = `${Math.round(position.top)}px`;
      savePosition();
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    let stickyFrame;
    const updateStickyOffset = () => {
      stickyFrame = null;
      if (floating.classList.contains('is-positioned')) {
        document.documentElement.style.removeProperty('--miva-wishlist-sticky-offset');
        return;
      }
      const sticky = document.querySelector('animate-sticky[animate] .product-sticky-cart');
      const rect = sticky?.getBoundingClientRect();
      const offset = rect?.height ? Math.ceil(rect.height + 14) : 0;
      document.documentElement.style.setProperty('--miva-wishlist-sticky-offset', `${offset}px`);
    };
    const requestStickyUpdate = () => {
      if (stickyFrame) return;
      stickyFrame = window.requestAnimationFrame(updateStickyOffset);
    };
    window.addEventListener('scroll', requestStickyUpdate, { passive: true });
    window.addEventListener('resize', requestStickyUpdate);
    requestStickyUpdate();
  };

  const initVariantSubscription = () => {
    if (window.MivaWishlistVariantSubscription || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    window.MivaWishlistVariantSubscription = subscribe(PUB_SUB_EVENTS.variantChange, (event) => updateProductVariant(event?.data?.variant));
  };

  const init = () => {
    root = document.querySelector('[data-miva-wishlist-root]');
    dialog = root?.querySelector('[data-miva-wishlist-dialog]');
    list = root?.querySelector('[data-miva-wishlist-list]');
    itemTemplate = document.querySelector('[data-miva-wishlist-item-template]');
    if (!root || !dialog || !list || !itemTemplate) return;

    items = readItems();
    syncInterface();
    initFloatingDrag();
    initVariantSubscription();
    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);
    document.addEventListener('keydown', trapKeyboard);
    document.addEventListener('shopify:section:load', syncInterface);
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      items = readItems();
      selectedIds = new Set([...selectedIds].filter((id) => isSaved(id)));
      syncInterface();
    });

    window.MivaWishlist = {
      initialized: true,
      open: openDrawer,
      close: closeDrawer,
      getItems: () => [...items]
    };
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
